import { useToast } from "@chakra-ui/toast";
import {
  ArchiveOutlined as ArchiveOutlinedIcon,
  ContentCopyOutlined as ContentCopyOutlinedIcon,
  DeleteOutlined as DeleteOutlinedIcon,
  DeveloperBoard as DeveloperBoardIcon,
  EditOutlined as EditOutlinedIcon,
  UnarchiveOutlined as UnarchiveOutlinedIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { FC, useCallback, useContext, useMemo } from "react";
import { graphql } from "react-relay";

import { PopupPatternTableActions_CopyMutation } from "~/src/__relay_artifacts__/PopupPatternTableActions_CopyMutation.graphql";
import { PopupPatternTableActions_DeleteMutation } from "~/src/__relay_artifacts__/PopupPatternTableActions_DeleteMutation.graphql";
import { TableActions } from "~/src/components/common/tables/TableActions";
import {
  useArchiveHandler,
  useUnarchiveHandler,
} from "~/src/components/features/archive";
import { DeleteConfirmDialog } from "~/src/components/features/global/DeleteConfirmDialog";
import { DateRangeContext } from "~/src/components/features/global/HeaderNavigation/DateRangeField";
import { useOpenState } from "~/src/lib/hooks";
import { useMutationCommit } from "~/src/lib/react-relay";

type Props = {
  siteSlug: string;
  popupGroupSlug: string;
  popup: { id: string; slug: string; name: string | null; isArchive: boolean };
  connectionId: string;
};

const deleteMutation = graphql`
  mutation PopupPatternTableActions_DeleteMutation(
    $input: DeletePopupInput!
    $connections: [ID!]!
  ) {
    deletePopup(input: $input) {
      deletedPopupId @deleteEdge(connections: $connections)
    }
  }
`;

const copyMutation = graphql`
  mutation PopupPatternTableActions_CopyMutation(
    $input: CopyPopupInput!
    $analyzerInput: ReportAnalyzerInput!
    $connections: [ID!]!
  ) {
    copyPopup(input: $input) {
      popup @appendNode(connections: $connections, edgeTypeName: "PopupEdge") {
        id
        slug
        name
        status
        isArchive
        kind
        judgeStatus
        weight
        clickCvr(input: $analyzerInput)
        clickCvUserCount(input: $analyzerInput)
        clickUserCount(input: $analyzerInput)
        clickUserRate(input: $analyzerInput)
        cvr(input: $analyzerInput)
        impressionCvUserCount(input: $analyzerInput)
        impressionCvr(input: $analyzerInput)
        impressionUserCount(input: $analyzerInput)
        readyCvUserCount(input: $analyzerInput)
        totalCvUserCount(input: $analyzerInput)
        totalUserCount(input: $analyzerInput)
        page {
          slug
          name
          funnel {
            name
            slug
          }
        }
      }
    }
  }
`;

export const PopupPatternTableAcitons: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ siteSlug, popupGroupSlug, popup, connectionId }) => {
  const toast = useToast();
  const {
    dateRange: [startOn, endOn],
  } = useContext(DateRangeContext);
  const { isOpen, onOpen, onClose } = useOpenState();

  const deleteMutate =
    useMutationCommit<PopupPatternTableActions_DeleteMutation>(deleteMutation);
  const copyMutate =
    useMutationCommit<PopupPatternTableActions_CopyMutation>(copyMutation);

  const handleArchive = useArchiveHandler(popup.id, connectionId);
  const handleUnarchive = useUnarchiveHandler(popup.id, connectionId);

  const handleDelete = useCallback(async () => {
    try {
      const res = await deleteMutate({
        variables: {
          input: {
            popupId: popup.id,
          },
          connections: [connectionId],
        },
      });
      const { deletedPopupId } = res.deletePopup || {};
      if (!deletedPopupId) throw new Error("assertion failed");

      toast({
        title: `${popup.name}を削除しました`,
        status: "success",
      });
    } catch (err) {
      toast({ title: "削除に失敗しました", status: "error" });
    }
  }, [connectionId, deleteMutate, popup.id, popup.name, toast]);

  const handleCopy = useCallback(async () => {
    try {
      const res = await copyMutate({
        variables: {
          input: {
            popupId: popup.id,
          },
          analyzerInput: {
            startOn: format(startOn, "yyyy-MM-dd"),
            endOn: format(endOn, "yyyy-MM-dd"),
          },
          connections: [connectionId],
        },
      });
      const copiedPopup = res.copyPopup?.popup;
      if (!copiedPopup) throw new Error("assertion failed");

      toast({
        title: `${popup.name}をコピーしました`,
        status: "success",
      });
    } catch (err) {
      toast({ title: "コピーに失敗しました", status: "error" });
    }
  }, [popup.id, popup.name, copyMutate, toast, startOn, endOn, connectionId]);

  const actionMenus = useMemo(
    () => [
      {
        label: "コンテンツレポート",
        icon: <DeveloperBoardIcon />,
        to: `/sites/${siteSlug}/popup_groups/${popupGroupSlug}/patterns/${popup.slug}/content_report`,
      },
      {
        label: "編集",
        icon: <EditOutlinedIcon />,
        to: `/sites/${siteSlug}/popup_groups/${popupGroupSlug}/patterns/${popup.slug}/edit`,
      },
      popup.isArchive
        ? [
            {
              label: "アーカイブから戻す",
              icon: <UnarchiveOutlinedIcon />,
              onClick: () => handleUnarchive(),
            },
            {
              label: "削除",
              icon: <DeleteOutlinedIcon />,
              onClick: () => onOpen(),
            },
          ]
        : [
            {
              label: "アーカイブ",
              icon: <ArchiveOutlinedIcon />,
              onClick: () => handleArchive(),
            },
            {
              label: "コピー",
              icon: <ContentCopyOutlinedIcon />,
              onClick: () => handleCopy(),
            },
          ],
    ],
    [
      handleArchive,
      handleCopy,
      handleUnarchive,
      onOpen,
      popup.isArchive,
      popup.slug,
      popupGroupSlug,
      siteSlug,
    ]
  );

  return (
    <>
      <TableActions actionMenus={actionMenus.flat()} />
      <DeleteConfirmDialog
        name={popup.name}
        isOpen={isOpen}
        onClose={onClose}
        onDelete={handleDelete}
      />
    </>
  );
};
